import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import { Popup } from '@atlaskit/popup/popup';
import type { TriggerProps } from '@atlaskit/popup/types';
import { Box } from '@atlaskit/primitives/compiled/box';

import { useQuery } from '@atlassian/conversation-assistant-service/common/utils/use-query';
import {
	createHookWithAssistanceService,
	type AssistanceServiceInjectedParams,
	type AssistanceServiceInputParam,
} from '@atlassian/conversation-assistant-service/services/assistance-service/main';
import type { Agent } from '@atlassian/conversation-assistant-service/types';

import { expValEquals } from '@confluence/feature-experiments';
import { useNativeCollabState } from '@confluence/native-collab/entry-points/useNativeCollabState.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';

import { AgentPresenceMenu } from './AgentPresenceMenu.tsx';
import { AgentPresenceAvatarGroup } from './AgentPresenceAvatarGroup.tsx';

const i18n = defineMessages({
	agentGroupLabel: {
		id: 'team-presence.agent-group.label',
		defaultMessage: 'Active agents on content',
		description: 'Accessible region landmark for agent presence avatar group',
	},
});

const getUniqueAgents = (agents: (Agent | undefined)[]) => {
	const uniqueAgents = new Map<string, Agent>();

	agents?.forEach((agent) => {
		if (agent) {
			uniqueAgents.set(agent.id, agent);
		}
	});

	return Array.from(uniqueAgents.values());
};

const getUniqueAgentIds = (agentIds: string[]) => Array.from(new Set(agentIds)).sort();

const getAgentIdFromParticipantUserId = (userId: string) => userId.replace('agent:', '');

type UseAgentsByIdsProps = {
	agentIds: string[];
} & AssistanceServiceInjectedParams;

// Shared by both experiment paths: subscribe to AI-provider presence and expose the sorted,
// de-duplicated set of active agent ids, re-rendering only when that set actually changes.
const useActiveAgentIds = (): string[] => {
	const [{ collabEditProvider }] = useNativeCollabState();
	const [activeAgentIds, setActiveAgentIds] = useState<string[]>([]);

	useEffect(() => {
		const handleUpdatePresence = () => {
			const agentIds =
				collabEditProvider
					?.getAIProviderParticipants()
					.map((agent) => getAgentIdFromParticipantUserId(agent.userId)) ?? [];

			setActiveAgentIds((prev) => {
				const next = getUniqueAgentIds(agentIds);
				return prev.length === next.length && prev.every((id, i) => id === next[i]) ? prev : next;
			});
		};

		handleUpdatePresence();

		collabEditProvider?.on('presence', handleUpdatePresence);

		return () => {
			collabEditProvider?.off('presence', handleUpdatePresence);
		};
	}, [collabEditProvider]);

	return activeAgentIds;
};

// Shared assistance-service params for the agent-detail lookups on both paths.
const buildAssistanceServiceParams = (cloudId: string) => ({
	siteId: cloudId,
	product: 'confluence' as const,
	experienceId: 'ai-mate' as const,
	touchpointSource: 'aiAgentPresence' as const,
});

// Shared presentation for both experiment paths: renders the active agents in an overflow popup,
// or nothing when there are none.
const AgentPresenceView = ({
	activeAgents,
}: {
	activeAgents: Agent[];
}): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isAgentGroupOverflowOpen, setIsAgentGroupOverflowOpen] = useState(false);

	const handleAgentGroupOverflowMenuClick = useCallback(
		(agentCount: number) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: 'aiAgentPresence',
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'aiAgentPresenceGroupOverflow',
					product: 'confluence',
					attributes: {
						agents: agentCount,
					},
				},
			}).fire();

			setIsAgentGroupOverflowOpen(
				(isAgentGroupOverflowOpenState) => !isAgentGroupOverflowOpenState,
			);
		},
		[createAnalyticsEvent, setIsAgentGroupOverflowOpen],
	);

	const handleAgentGroupOverflowMenuClose = useCallback(() => {
		setIsAgentGroupOverflowOpen(false);
	}, [setIsAgentGroupOverflowOpen]);

	const renderAgentGroupOverflowMenu = useCallback(() => {
		return <AgentPresenceMenu agents={activeAgents} />;
	}, [activeAgents]);

	if (!activeAgents?.length) {
		return null;
	}

	return (
		<Popup
			placement="bottom-end"
			isOpen={isAgentGroupOverflowOpen}
			onClose={handleAgentGroupOverflowMenuClose}
			content={renderAgentGroupOverflowMenu}
			trigger={(triggerProps: TriggerProps) => (
				<Box
					{...triggerProps}
					paddingInline="space.025"
					aria-label={formatMessage(i18n.agentGroupLabel)}
					role="region"
				>
					<AgentPresenceAvatarGroup
						agents={activeAgents}
						isOpen={isAgentGroupOverflowOpen}
						onMoreClick={() => handleAgentGroupOverflowMenuClick(activeAgents.length)}
					/>
				</Box>
			)}
		/>
	);
};

// ---------------------------------------------------------------------------
// Off path (`platform_editor_agent_be_streaming` disabled): pre-existing behaviour.
// Resolve via getAgentDetails only and drop lookups that fail. Delete this pair when the
// experiment is cleaned up.
// ---------------------------------------------------------------------------

const useAgentsByIds: (
	props: Omit<UseAgentsByIdsProps, 'assistanceServiceInjectedParams'> & AssistanceServiceInputParam,
) => Agent[] = createHookWithAssistanceService(
	({ agentIds, assistanceServiceInjectedParams: { assistanceService } }: UseAgentsByIdsProps) => {
		const { data } = useQuery({
			queryKey: ['agentPresenceAgentsByIds', ...agentIds.slice().sort()],
			queryFn: async () => {
				const agentResults = await Promise.allSettled(
					agentIds.map((agentId) => assistanceService.getAgentDetails(agentId)),
				);

				return agentResults.flatMap((result) =>
					result.status === 'fulfilled' ? [result.value] : [],
				);
			},
			enabled: agentIds.length > 0,
		});

		return data ?? [];
	},
);

export const AgentPresenceComponent = (): React.JSX.Element | null => {
	const { cloudId } = useSessionData();
	const activeAgentIds = useActiveAgentIds();

	const agentsFromAssistanceService = useAgentsByIds({
		agentIds: activeAgentIds,
		assistanceServiceParams: buildAssistanceServiceParams(cloudId),
	});

	// Cache resolved agent details by ID so presence changes can continue to render
	// already-known active agents while the details for newly-active agents are loading.
	// The rendered list is still derived from activeAgentIds, which prevents stale agents
	// from a previous active set appearing after an agent changes.
	const [agentsById, setAgentsById] = useState(() => new Map<string, Agent>());

	useEffect(() => {
		setAgentsById((previousAgentsById) => {
			let nextAgentsById: Map<string, Agent> | undefined;

			agentsFromAssistanceService.forEach((agent) => {
				if (previousAgentsById.get(agent.id) !== agent) {
					nextAgentsById ??= new Map(previousAgentsById);
					nextAgentsById.set(agent.id, agent);
				}
			});

			return nextAgentsById ?? previousAgentsById;
		});
	}, [agentsFromAssistanceService]);

	const activeAgents = useMemo(() => {
		return getUniqueAgents(
			activeAgentIds.map((agentId) => {
				return (
					agentsFromAssistanceService.find((agent) => agent.id === agentId) ??
					agentsById.get(agentId)
				);
			}),
		);
	}, [activeAgentIds, agentsById, agentsFromAssistanceService]);

	return <AgentPresenceView activeAgents={activeAgents} />;
};

// ---------------------------------------------------------------------------
// On path (`platform_editor_agent_be_streaming` enabled): agent-edit presence.
// Remote edit steps carry AAIDs (resolved via the identity-account endpoint) and locally-selected
// agents carry Rovo UUIDs (resolved via getAgentDetails), so we try the UUID endpoint, then the AAID
// one, then fall back to a generic agent. Every settled id renders (keyed by the id we asked for)
// rather than being dropped. Delete this section when the experiment is cleaned up.
// ---------------------------------------------------------------------------

// Human-readable fallback labels for agents we can't resolve (type-only ids / lookup miss).
const AGENT_TYPE_LABELS: Record<string, string> = {
	mcp: 'MCP',
	twg: 'TWG',
};

// A synthetic Agent for an id that can't be resolved (a type-only `mcp`/`twg` id, or an
// AAID whose lookup failed) so it still renders a generic avatar. With no real icon or
// identity_account_id, AgentAvatar draws a deterministic generic avatar.
const createFallbackAgent = (agentId: string): Agent => ({
	id: agentId,
	identity_account_id: null,
	named_id: agentId,
	name: AGENT_TYPE_LABELS[agentId] ?? 'Agent',
	description: null,
	creator_type: 'CUSTOMER',
	available_plugins: {},
	is_default: false,
	actor_type: 'AGENT',
	user_defined_conversation_starters: null,
	favourite: false,
	deactivated: false,
});

// Resolve each active id to an Agent, keyed by the id we asked for. Returns `null` while the lookup
// is in flight, so callers can withhold rendering an agent until it resolves.
const useAgentsByIdsWithAgentEdits: (
	props: Omit<UseAgentsByIdsProps, 'assistanceServiceInjectedParams'> & AssistanceServiceInputParam,
) => Map<string, Agent> | null = createHookWithAssistanceService(
	({ agentIds, assistanceServiceInjectedParams: { assistanceService } }: UseAgentsByIdsProps) => {
		const { data } = useQuery({
			queryKey: ['agentPresenceAgentsByIds', ...agentIds.slice().sort()],
			queryFn: async () => {
				const entries = await Promise.all(
					agentIds.map(async (agentId): Promise<[string, Agent]> => {
						try {
							return [agentId, await assistanceService.getAgentDetails(agentId)];
						} catch {
							try {
								return [
									agentId,
									await assistanceService.getAgentDetailsByIdentityAccountId(agentId),
								];
							} catch {
								return [agentId, createFallbackAgent(agentId)];
							}
						}
					}),
				);
				return new Map(entries);
			},
			enabled: agentIds.length > 0,
		});

		return data ?? null;
	},
);

const AgentPresenceComponentWithAgentEdits = (): React.JSX.Element | null => {
	const { cloudId } = useSessionData();
	const activeAgentIds = useActiveAgentIds();

	const resolvedAgentsById = useAgentsByIdsWithAgentEdits({
		agentIds: activeAgentIds,
		assistanceServiceParams: buildAssistanceServiceParams(cloudId),
	});

	// Cache settled lookups (keyed by the id we asked for) so already-shown agents survive
	// the re-query that runs when a new agent joins, instead of blinking out until it resolves.
	const [agentsById, setAgentsById] = useState(() => new Map<string, Agent>());

	useEffect(() => {
		if (!resolvedAgentsById) {
			return;
		}
		setAgentsById((previous) => {
			let next: Map<string, Agent> | undefined;
			resolvedAgentsById.forEach((agent, agentId) => {
				if (previous.get(agentId) !== agent) {
					if (!next) {
						next = new Map(previous);
					}
					next.set(agentId, agent);
				}
			});
			return next ?? previous;
		});
	}, [resolvedAgentsById]);

	// Only render an agent once its lookup has settled (a resolved agent, or a generic
	// fallback for a type-only/unresolvable id). Ids still in flight resolve to `undefined` and are
	// dropped by getUniqueAgents, so nothing flickers in as a placeholder first.
	const activeAgents = useMemo(
		() =>
			getUniqueAgents(
				activeAgentIds.map(
					(agentId) => resolvedAgentsById?.get(agentId) ?? agentsById.get(agentId),
				),
			),
		[activeAgentIds, agentsById, resolvedAgentsById],
	);

	return <AgentPresenceView activeAgents={activeAgents} />;
};

export const AgentPresence = (): React.JSX.Element | null => {
	const { isAdminHubAIEnabled, isAIEarlyAccessEnabled } = useSessionData();

	if (
		!isAdminHubAIEnabled ||
		!isAIEarlyAccessEnabled ||
		!expValEquals('reinstate_confluence_agent_presence', 'isEnabled', true)
	) {
		return null;
	}

	return (
		<ErrorBoundary attribution={Attribution.COLLABORATION_PRESENCE}>
			{expValEquals('platform_editor_agent_be_streaming', 'isEnabled', true) ? (
				<AgentPresenceComponentWithAgentEdits data-vc="aiAgentPresence" />
			) : (
				<AgentPresenceComponent data-vc="aiAgentPresence" />
			)}
		</ErrorBoundary>
	);
};
